import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Globe, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const FinancialTicker = () => {
  const [marketData, setMarketData] = useState({
    stocks: [
      { symbol: 'S&P 500', value: 4783.45, change: 1.24, changePercent: 0.026 },
      { symbol: 'DOW', value: 37305.16, change: 157.06, changePercent: 0.42 },
      { symbol: 'NASDAQ', value: 14813.92, change: 28.51, changePercent: 0.19 },
      { symbol: 'FTSE 100', value: 7733.24, change: -12.45, changePercent: -0.16 },
    ],
    forex: [
      { pair: 'EUR/USD', value: 1.0952, change: 0.0012, changePercent: 0.11 },
      { pair: 'GBP/USD', value: 1.2734, change: -0.0023, changePercent: -0.18 },
      { pair: 'USD/JPY', value: 147.82, change: 0.45, changePercent: 0.31 },
      { pair: 'USD/CHF', value: 0.8456, change: 0.0008, changePercent: 0.09 },
    ],
    crypto: [
      { symbol: 'BTC', name: 'Bitcoin', value: 51234.56, change: 1456.23, changePercent: 2.93 },
      { symbol: 'ETH', name: 'Ethereum', value: 2845.67, change: -45.89, changePercent: -1.59 },
      { symbol: 'BNB', name: 'Binance', value: 345.12, change: 12.34, changePercent: 3.71 },
    ]
  });

  const [worldTime, setWorldTime] = useState({
    newYork: new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true }),
    london: new Date().toLocaleString('en-US', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: true }),
    tokyo: new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit', hour12: true }),
    sydney: new Date().toLocaleString('en-US', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit', hour12: true }),
  });

  useEffect(() => {
    // Update times every minute
    const timeInterval = setInterval(() => {
      setWorldTime({
        newYork: new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true }),
        london: new Date().toLocaleString('en-US', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: true }),
        tokyo: new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit', hour12: true }),
        sydney: new Date().toLocaleString('en-US', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit', hour12: true }),
      });
    }, 60000);

    // Simulate real-time market updates
    const marketInterval = setInterval(() => {
      setMarketData(prev => ({
        stocks: prev.stocks.map(stock => ({
          ...stock,
          value: stock.value + (Math.random() - 0.5) * 10,
          change: (Math.random() - 0.5) * 20,
          changePercent: (Math.random() - 0.5) * 2
        })),
        forex: prev.forex.map(pair => ({
          ...pair,
          value: pair.value + (Math.random() - 0.5) * 0.01,
          change: (Math.random() - 0.5) * 0.01,
          changePercent: (Math.random() - 0.5) * 0.5
        })),
        crypto: prev.crypto.map(coin => ({
          ...coin,
          value: coin.value + (Math.random() - 0.5) * 100,
          change: (Math.random() - 0.5) * 200,
          changePercent: (Math.random() - 0.5) * 5
        }))
      }));
    }, 5000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(marketInterval);
    };
  }, []);

  const MarketItem = ({ item, type }) => {
    const isPositive = item.change >= 0;
    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3">
          {isPositive ? (
            <TrendingUp className="w-5 h-5 text-green-600" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-600" />
          )}
          <div>
            <p className="font-bold text-gray-900">{item.symbol || item.pair || item.name}</p>
            {type === 'crypto' && <p className="text-xs text-gray-500">{item.symbol}</p>}
          </div>
        </div>
        <div className="text-right">
          <p className="font-semibold text-gray-900">
            {type === 'forex' ? item.value.toFixed(4) : item.value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </p>
          <p className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '+' : ''}{item.changePercent.toFixed(2)}%
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Global Financial Markets</h2>
          <p className="text-lg text-gray-600">Real-time updates from major markets worldwide</p>
        </div>

        {/* World Time Clocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { city: 'New York', time: worldTime.newYork, status: 'NYSE Open' },
            { city: 'London', time: worldTime.london, status: 'LSE Open' },
            { city: 'Tokyo', time: worldTime.tokyo, status: 'TSE Closed' },
            { city: 'Sydney', time: worldTime.sydney, status: 'ASX Closed' }
          ].map((zone) => (
            <Card key={zone.city} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <h3 className="font-bold text-gray-900">{zone.city}</h3>
                <p className="text-2xl font-bold text-blue-600 my-2">{zone.time}</p>
                <p className="text-xs text-gray-600">{zone.status}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Market Data */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Stocks */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">Stock Indices</h3>
            </div>
            <div className="space-y-3">
              {marketData.stocks.map((stock, idx) => (
                <MarketItem key={idx} item={stock} type="stock" />
              ))}
            </div>
          </div>

          {/* Forex */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-6 h-6 text-green-600" />
              <h3 className="text-xl font-bold text-gray-900">Foreign Exchange</h3>
            </div>
            <div className="space-y-3">
              {marketData.forex.map((pair, idx) => (
                <MarketItem key={idx} item={pair} type="forex" />
              ))}
            </div>
          </div>

          {/* Crypto */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-6 h-6 text-purple-600" />
              <h3 className="text-xl font-bold text-gray-900">Cryptocurrency</h3>
            </div>
            <div className="space-y-3">
              {marketData.crypto.map((coin, idx) => (
                <MarketItem key={idx} item={coin} type="crypto" />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            * Market data is simulated for demonstration purposes. Updates every 5 seconds.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FinancialTicker;
